import { cs } from '../../lib/currency';
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

/**
 * TournamentAmountChangeConfirmDialog (iter240)
 *
 * Shows the impact preview when admin changes a tournament's
 * `initial_payment_amount`. Three classes of impact:
 *   - update_unpaid     — registration's invoice will be updated + email resent.
 *   - additional_charge — a manual invoice will be created for the delta.
 *   - refund            — a refund will be issued for the delta.
 *
 * The admin must confirm before the cascade executes.
 */
export default function TournamentAmountChangeConfirmDialog({
  open,
  onOpenChange,
  preview,           // backend preview payload
  onConfirm,         // async () => {}
  submitting = false,
}) {
  if (!preview) return null;

  const counts = preview.counts || {};
  const rows = preview.rows || [];

  const fmt = (v) => `${cs()}${Number(v || 0).toFixed(2)}`;

  const refundCount = counts.refund || 0;
  const chargeCount = counts.additional_charge || 0;
  const updateUnpaidCount = counts.update_unpaid || 0;

  const actionRows = rows.filter((r) => r.action !== 'noop');
  const noopCount = (counts.noop || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="tournament-amount-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-5 w-5" />
            Confirm tournament fee change
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium">{preview.tournament_name}</span>
            <span className="mx-2 text-slate-400">·</span>
            <span className="text-slate-700 font-medium">
              {fmt(preview.old_amount)} → <span className="font-semibold text-emerald-700">{fmt(preview.new_amount)}</span>
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3 py-2">
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
            <div className="text-xs text-blue-700">Invoices to update</div>
            <div className="text-2xl font-semibold text-blue-900">{updateUnpaidCount}</div>
            <div className="text-[11px] text-blue-700">unpaid · email re-sent</div>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs text-amber-700">Additional charges</div>
            <div className="text-2xl font-semibold text-amber-900">{chargeCount}</div>
            <div className="text-[11px] text-amber-700">
              {fmt(preview.total_additional_charge_amount)} via manual invoice
            </div>
          </div>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-xs text-emerald-700">Refunds to issue</div>
            <div className="text-2xl font-semibold text-emerald-900">{refundCount}</div>
            <div className="text-[11px] text-emerald-700">
              {fmt(preview.total_refund_amount)} total
            </div>
          </div>
        </div>

        {actionRows.length > 0 && (
          <details
            open
            className="rounded-md border border-slate-200 bg-slate-50"
            data-testid="tournament-amount-change-detail"
          >
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">
              Per-registration impact ({actionRows.length})
              {noopCount > 0 && (
                <span className="ml-2 text-xs text-slate-500">
                  · {noopCount} unchanged
                </span>
              )}
            </summary>
            <div className="max-h-72 overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow>
                    <TableHead>Player</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead className="text-right">Old</TableHead>
                    <TableHead className="text-right">New</TableHead>
                    <TableHead className="text-right">Δ</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {actionRows.map((row) => (
                    <TableRow
                      key={row.registration_id}
                      data-testid={`tournament-amount-row-${row.registration_id}`}
                    >
                      <TableCell className="font-medium">{row.player_name || '—'}</TableCell>
                      <TableCell className="text-xs text-slate-600">{row.parent_name || '—'}</TableCell>
                      <TableCell className="text-right text-slate-600">{fmt(row.old_amount)}</TableCell>
                      <TableCell className="text-right font-medium">{fmt(row.new_amount)}</TableCell>
                      <TableCell
                        className={`text-right font-mono text-xs ${
                          row.delta > 0
                            ? 'text-amber-700'
                            : row.delta < 0
                            ? 'text-emerald-700'
                            : 'text-slate-500'
                        }`}
                      >
                        {row.delta > 0 ? '+' : ''}{fmt(row.delta)}
                      </TableCell>
                      <TableCell>
                        {row.action === 'update_unpaid' && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]">
                            update + email
                          </Badge>
                        )}
                        {row.action === 'additional_charge' && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                            invoice {fmt(row.amount_to_charge)}
                          </Badge>
                        )}
                        {row.action === 'refund' && (
                          <div className="flex flex-col items-start gap-0.5">
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                              <RefreshCw className="h-2.5 w-2.5 mr-0.5 inline-block" />
                              refund {fmt(row.amount_to_refund)}
                            </Badge>
                            {row.surcharge_refund_amount > 0 && (
                              <span className="text-[10px] text-slate-500" data-testid={`refund-breakdown-${row.registration_id}`}>
                                {fmt(row.base_refund_amount)} + {fmt(row.surcharge_refund_amount)} cc fee
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </details>
        )}

        <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
          <p className="font-medium text-amber-900 mb-1">This cannot be undone automatically.</p>
          <ul className="list-disc list-inside space-y-0.5 text-amber-800">
            {refundCount > 0 && (
              <li>{refundCount} refund(s) will be issued to original payment methods (Stripe falls back to account credit if the original charge cannot be located).</li>
            )}
            {chargeCount > 0 && (
              <li>{chargeCount} new manual invoice(s) will be created and emailed to the parent.</li>
            )}
            {updateUnpaidCount > 0 && (
              <li>{updateUnpaidCount} unpaid invoice(s) will be updated and the reminder email re-sent.</li>
            )}
            {actionRows.length === 0 && (
              <li>No registrations are affected — the new amount matches the old amount.</li>
            )}
          </ul>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="cancel-tournament-amount-change"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="confirm-tournament-amount-change"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Applying…
              </>
            ) : (
              actionRows.length === 0 ? 'Save anyway' : 'Confirm & apply'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
